import {
  NuorderBrand,
  SelectedDropdownOption,
  Shop,
  Vendor,
} from 'root/apps/pages/views/inventory/Vendor/b2b/interfaces';

/**
 * Finds a matching vendor from a list of vendors based on the provided brand ID.
 *
 * @param {string} brandId - The unique identifier for the brand to match.
 * @param {Vendor[]} vendorList - The list of vendors to search through.
 * @returns {Vendor | undefined} - The matching vendor object if found, otherwise undefined.
 */
export const findMatchingVendor = (brandId: string, vendorList: Vendor[]): Vendor | undefined => {
  return vendorList.find((v) => brandId === v.b2bSellerUID);
};

/**
 * Fetches the NuORDER base URL from the global config.
 * @returns {Promise<string>} The NuORDER base URL.
 */
export const fetchNuorderBaseUrl = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    window.lsCloud.Config.access('b2b_web_service_base_uri')
      .done((result: string) => resolve(result))
      .fail((err: unknown) => reject(err));
  });
};

/**
 * Formats a shop's address into a single string.
 *
 * This function takes a `Shop` object, extracts the address details from it,
 * filters out any falsy values (e.g., `null`, `undefined`, or empty strings),
 * and joins the remaining parts with a comma to create a formatted address string.
 *
 * @param {Shop} shop - The shop object containing the address information.
 * @returns {string} - A formatted address string with non-empty parts joined by commas.
 */
export const formatShopAddress = (shop: Shop): string => {
  const address = shop.Contact.Addresses.ContactAddress;
  if (!address) {
    if (shop.name) {
      return shop.name;
    }
    return 'N/A';
  }
  return [shop.name, address.address1, address.city, address.state, address.zip, address.country]
    .filter(Boolean)
    .join(', ');
};

/**
 * Generates a list of dropdown options for linked vendors based on the provided brand and vendor lists.
 * This is a utility function because all this function does is transform the data without performing
 * any additional logic. i.e., API calls, business logic, etc.
 *
 * @param {NuorderBrand[]} brandList - The list of NuORDER brands.
 * @param {Vendor[]} vendorList - The list of vendors.
 * @returns {SelectedDropdownOption[]} - An array of dropdown options for linked vendors.
 */
export const getLinkedVendorDropdownOptions = (
  brandList: NuorderBrand[],
  vendorList: Vendor[]
): SelectedDropdownOption[] => {
  return brandList.reduce<SelectedDropdownOption[]>((acc, nuOrderBrand) => {
    const matchingVendor = findMatchingVendor(nuOrderBrand.id, vendorList);
    if (matchingVendor) {
      acc.push({
        label: matchingVendor.name,
        value: Number(matchingVendor.vendorID),
      });
    }
    return acc;
  }, []);
};

/**
 * Constant representing the case where no vendor is selected.
 */
export const NO_VENDOR_SELECTED: number = 0;

/**
 * Retrieves the account ID from the session.
 * @returns {number | null} The account ID, or null if conversion fails.
 */
export const getAccountId = (): number | null => {
  const id = window.merchantos.session.getSystemCustomerID();
  const num = Number(id);
  return Number.isNaN(num) ? null : num;
};

/**
 * Retrieves the merchant name from the session.
 * @returns {string | null} The merchant name, or null if not available.
 */
export const getMerchantName = (): string | null =>
  window.merchantos.session.getSystemCustomerName();
